import React, { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

export default function CobrancaFlexBanner({ planoInfo, driverUid, bloqueado }) {
  const [cobranca, setCobranca] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQr, setShowQr] = useState(false);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!driverUid || !planoInfo || planoInfo.plano !== "flex") {
      setLoading(false);
      return undefined;
    }
    const hoje = new Date().toISOString().slice(0, 10);
    const unsub = onSnapshot(
      doc(db, "driver_cobrancas", `${driverUid}_${hoje}`),
      (snap) => {
        setCobranca(snap.exists() ? snap.data() : null);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [driverUid, planoInfo]);

  const copiarPix = () => {
    if (!cobranca?.pixQrCode) return;
    navigator.clipboard.writeText(cobranca.pixQrCode).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 3000);
    });
  };

  if (!planoInfo || planoInfo.plano !== "flex" || loading) return null;
  if (planoInfo.trialAtivo || planoInfo.emTrial) return null;

  if (bloqueado) {
    return (
      <Shell tone="danger">
        <Header icon="!" title="Conta bloqueada" subtitle="Pague a taxa pendente para desbloquear" color="#ef4444" />
        {cobranca?.totalTaxa > 0 && (
          <div style={styles.body}>
            <AmountRow title="Taxa pendente" amount={cobranca.totalTaxa} rightTitle="Corridas" rightValue={cobranca.totalCorridas || 0} color="#ef4444" />
            <PixArea cobranca={cobranca} showQr={showQr} setShowQr={setShowQr} copiarPix={copiarPix} copiado={copiado} color="#ef4444" />
            <p style={styles.note}>Desbloqueio automatico apos confirmacao do pagamento.</p>
          </div>
        )}
      </Shell>
    );
  }

  if (cobranca && cobranca.status === "cobrado" && cobranca.totalTaxa > 0) {
    const prazoHora = cobranca.pixVencimento
      ? new Date(cobranca.pixVencimento).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
      : "12:00";
    return (
      <Shell tone="warning">
        <Header icon="$" title={`Taxa de ontem - pague ate ${prazoHora}`} color="#f59e0b" />
        <div style={styles.body}>
          <AmountRow
            title="Taxa Zuno (8%)"
            amount={cobranca.totalTaxa}
            rightTitle="Suas corridas"
            rightValue={cobranca.totalCorridas || 0}
            rightSubtitle={`de R$ ${(cobranca.totalBruto || 0).toFixed(2)}`}
            color="#f59e0b"
          />
          <PixArea cobranca={cobranca} showQr={showQr} setShowQr={setShowQr} copiarPix={copiarPix} copiado={copiado} color="#f59e0b" />
        </div>
      </Shell>
    );
  }

  if (cobranca && cobranca.totalCorridas > 0 && cobranca.status === "pendente") {
    return (
      <div style={styles.summary}>
        <span style={{ fontSize: 20, flexShrink: 0 }}>%</span>
        <div style={{ flex: 1 }}>
          <p style={styles.summaryTitle}>
            Hoje: {cobranca.totalCorridas} corrida{cobranca.totalCorridas !== 1 ? "s" : ""} - Taxa acumulada R$ {(cobranca.totalTaxa || 0).toFixed(2)}
          </p>
          <p style={styles.summaryText}>A cobranca e gerada automaticamente a meia-noite.</p>
        </div>
      </div>
    );
  }

  return null;
}

function Shell({ tone, children }) {
  const color = tone === "danger" ? "239,68,68" : "245,158,11";
  return (
    <div style={{ margin: "8px 16px", background: `rgba(${color},0.08)`, border: `1px solid rgba(${color},0.28)`, borderRadius: 16, overflow: "hidden" }}>
      {children}
    </div>
  );
}

function Header({ icon, title, subtitle, color }) {
  return (
    <div style={{ background: `${color}22`, padding: "12px 16px", display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 16, color }}>{icon}</span>
      <div>
        <p style={{ fontSize: 13, fontWeight: 700, color, margin: 0 }}>{title}</p>
        {subtitle && <p style={{ fontSize: 11, color: `${color}cc`, margin: 0 }}>{subtitle}</p>}
      </div>
    </div>
  );
}

function AmountRow({ title, amount, rightTitle, rightValue, rightSubtitle, color }) {
  return (
    <div style={styles.amountRow}>
      <div>
        <p style={styles.muted}>{title}</p>
        <p style={{ fontSize: 22, fontWeight: 800, color, margin: 0 }}>R$ {(amount || 0).toFixed(2)}</p>
      </div>
      <div style={{ textAlign: "right" }}>
        <p style={styles.muted}>{rightTitle}</p>
        <p style={styles.rightValue}>{rightValue}</p>
        {rightSubtitle && <p style={styles.summaryText}>{rightSubtitle}</p>}
      </div>
    </div>
  );
}

function PixArea({ cobranca, showQr, setShowQr, copiarPix, copiado, color }) {
  if (!cobranca.pixQrCode) {
    return <p style={styles.pendingPayment}>Cobranca aguardando configuracao de pagamento empresarial.</p>;
  }
  return (
    <>
      <button onClick={() => setShowQr(!showQr)} style={{ ...styles.pixButton, borderColor: `${color}55`, background: showQr ? `${color}1a` : `${color}26`, color }}>
        {showQr ? "Fechar Pix" : "Pagar via Pix"}
      </button>
      {showQr && <QrCodePix pixCode={cobranca.pixQrCode} onCopiar={copiarPix} copiado={copiado} cor={color} prazo={cobranca.pixVencimento} />}
    </>
  );
}

function QrCodePix({ pixCode, onCopiar, copiado, cor, prazo }) {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(pixCode)}&size=180x180&margin=6&ecc=M`;
  const prazoLabel = prazo
    ? new Date(prazo).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <div style={styles.qrBox}>
      <div style={styles.qrFrame}>
        <img src={qrUrl} alt="QR Code Pix" style={{ width: 180, height: 180 }} />
      </div>
      {prazoLabel && <p style={{ ...styles.note, color: cor }}>Vence {prazoLabel}</p>}
      <p style={styles.copyLabel}>Copie o codigo Pix:</p>
      <div style={styles.copyCode}>{pixCode?.slice(0, 80)}{pixCode?.length > 80 ? "..." : ""}</div>
      <button onClick={onCopiar} style={{ ...styles.copyButton, borderColor: copiado ? "rgba(34,197,94,0.3)" : `${cor}44`, background: copiado ? "rgba(34,197,94,0.2)" : `${cor}22`, color: copiado ? "#22c55e" : cor }}>
        {copiado ? "Copiado" : "Copiar codigo Pix"}
      </button>
    </div>
  );
}

const styles = {
  body: { padding: "14px 16px" },
  amountRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  muted: { fontSize: 11, color: "rgba(255,255,255,0.4)", margin: "0 0 2px" },
  rightValue: { fontSize: 16, fontWeight: 700, color: "#fff", margin: 0 },
  pixButton: { width: "100%", padding: "11px", borderRadius: 12, border: "1px solid", fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 12 },
  pendingPayment: { fontSize: 11, color: "rgba(255,255,255,0.45)", textAlign: "center", margin: "8px 0 0" },
  note: { fontSize: 11, color: "rgba(255,255,255,0.3)", textAlign: "center", margin: "8px 0 0" },
  qrBox: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 14, marginBottom: 4 },
  qrFrame: { width: 180, height: 180, margin: "0 auto 10px", background: "#fff", borderRadius: 10, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" },
  copyLabel: { fontSize: 10, color: "rgba(255,255,255,0.3)", margin: "0 0 4px" },
  copyCode: { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "8px 10px", marginBottom: 8, fontSize: 10, color: "rgba(255,255,255,0.4)", wordBreak: "break-all", fontFamily: "monospace", lineHeight: 1.5 },
  copyButton: { width: "100%", padding: 10, borderRadius: 10, border: "1px solid", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  summary: { margin: "8px 16px", background: "rgba(168,85,247,0.06)", border: "1px solid rgba(168,85,247,0.15)", borderRadius: 14, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 },
  summaryTitle: { fontSize: 12, fontWeight: 700, color: "#a855f7", margin: "0 0 2px" },
  summaryText: { fontSize: 11, color: "rgba(255,255,255,0.35)", margin: "2px 0 0" },
};
